import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Alert,
  IconButton,
  InputAdornment,
  Typography,
  Box,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  Chip,
} from '@mui/material'
import { LocationOn as LocationOnIcon } from '@mui/icons-material'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { authApi } from '../../api/authApi'
import { organizationApi } from '../../api/organizationApi'
import LocationPickerModal from '../common/LocationPickerModal'

// Updated schema to match the new API structure
const schema = yup.object({
  name: yup.string().required('Organization name is required'),
  address: yup.string().required('Address is required'),
  contact_name: yup.string().required('Contact name is required'),
  contact_phone: yup.string()
    .matches(/^[0-9]{10}$/, 'Phone must be 10 digits')
    .required('Contact phone is required'),
  contact_email: yup.string().email('Invalid email').required('Contact email is required'),
  subscription_plan: yup.string().oneOf(['basic', 'premium', 'enterprise']).default('basic'),
})

const RegisterOrgForm = ({ open, onClose, onSuccess, showSnackbar, editMode = false, initialData = null }) => {
  const theme = useTheme()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [locationPickerOpen, setLocationPickerOpen] = useState(false)
  const [geoLocation, setGeoLocation] = useState({ latitude: null, longitude: null, geofence_radius: 100 })

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      address: '',
      contact_name: '',
      contact_phone: '',
      contact_email: '',
      subscription_plan: 'basic',
    }
  })

  // If in edit mode, set form values when initialData changes
  useEffect(() => {
    if (editMode && initialData) {
      Object.keys(initialData).forEach(key => {
        if (key in schema.fields) {
          setValue(key, initialData[key])
        }
      })
      // Pre-populate geolocation if available
      if (initialData.latitude && initialData.longitude) {
        setGeoLocation({
          latitude: initialData.latitude,
          longitude: initialData.longitude,
          geofence_radius: initialData.geofence_radius || 100,
        })
      }
    } else if (!editMode) {
      // Reset form when opening in add mode
      reset({
        name: '',
        address: '',
        contact_name: '',
        contact_phone: '',
        contact_email: '',
        subscription_plan: 'basic',
      })
      setGeoLocation({ latitude: null, longitude: null, geofence_radius: 100 })
    }
  }, [editMode, initialData, setValue, reset])

  const onSubmit = async (data) => {
    setLoading(true)
    setError('')

    // Merge geolocation data into the payload
    const payload = {
      ...data,
      ...(geoLocation.latitude !== null && {
        latitude: geoLocation.latitude,
        longitude: geoLocation.longitude,
        geofence_radius: geoLocation.geofence_radius,
      }),
    }

    // Check for changes in edit mode
    if (editMode && initialData) {
      const hasChanges =
        data.name !== initialData.name ||
        data.address !== initialData.address ||
        data.contact_name !== initialData.contact_name ||
        data.contact_phone !== initialData.contact_phone ||
        data.contact_email !== initialData.contact_email ||
        data.subscription_plan !== initialData.subscription_plan ||
        geoLocation.latitude !== initialData.latitude ||
        geoLocation.longitude !== initialData.longitude ||
        geoLocation.geofence_radius !== initialData.geofence_radius

      if (!hasChanges) {
        showSnackbar && showSnackbar('No changes made', 'info')
        setLoading(false)
        return
      }
    }

    try {

      // Response structure: { id, name, status, message, created_at }
      if (editMode) {
        const response = await organizationApi.updateOrganization(initialData.id, payload)
        onSuccess && onSuccess(response.data)
        handleClose()
      } else {
        // In register mode, show success message with organization details
        const response = await authApi.registerOrganization(payload)
        onSuccess && onSuccess(response.data)
        handleClose()
      }

    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.response?.data?.message || 'Registration failed'
      setError(errorMsg)
      console.error('Registration error:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    reset()
    setError('')
    setGeoLocation({ latitude: null, longitude: null, geofence_radius: 100 })
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableRestoreFocus
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ borderBottom: `1px solid ${theme.palette.divider}`, pb: 2 }}>
        <Box sx={{ fontWeight: 'bold', fontSize: '1.25rem', mb: 0.5 }}>
          {editMode ? 'Edit Organization' : 'Register New Organization'}
        </Box>
        <Typography variant="body2" color="textSecondary">
          {editMode ? 'Update organization details' : 'Fill in organization details'}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        <form id="org-form" onSubmit={handleSubmit(onSubmit)}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Organization Name *"
                fullWidth
                {...register('name')}
                error={!!errors.name}
                helperText={errors.name?.message}
                disabled={loading}
                margin="normal"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Contact Name *"
                fullWidth
                {...register('contact_name')}
                error={!!errors.contact_name}
                helperText={errors.contact_name?.message}
                disabled={loading}
                margin="normal"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Contact Email *"
                type="email"
                fullWidth
                {...register('contact_email')}
                error={!!errors.contact_email}
                helperText={errors.contact_email?.message}
                disabled={loading}
                margin="normal"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Contact Phone *"
                fullWidth
                {...register('contact_phone')}
                error={!!errors.contact_phone}
                helperText={errors.contact_phone?.message}
                disabled={loading}
                margin="normal"
                InputProps={{
                  startAdornment: <InputAdornment position="start">+91</InputAdornment>,
                }}
              />
            </Grid>

            <Grid size={12}>
              <TextField
                label="Address *"
                fullWidth
                {...register('address')}
                error={!!errors.address}
                helperText={errors.address?.message}
                disabled={loading}
                margin="normal"
                placeholder="Enter organization address"
              />
            </Grid>

            {/* Geofence Location Picker */}
            <Grid size={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mt: 0.5 }}>
                <Button
                  variant="outlined"
                  startIcon={<LocationOnIcon />}
                  onClick={() => setLocationPickerOpen(true)}
                  disabled={loading}
                  color={geoLocation.latitude ? 'success' : 'primary'}
                >
                  {geoLocation.latitude ? 'Change Location' : 'Set Geofence Location'}
                </Button>
                {geoLocation.latitude && (
                  <Chip
                    icon={<LocationOnIcon fontSize="small" />}
                    label={`${parseFloat(geoLocation.latitude).toFixed(4)}, ${parseFloat(geoLocation.longitude).toFixed(4)} · ${geoLocation.geofence_radius}m radius`}
                    color="success"
                    variant="outlined"
                    size="small"
                    onDelete={() => setGeoLocation({ latitude: null, longitude: null, geofence_radius: 100 })}
                  />
                )}
              </Box>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                Set the office GPS location and radius for employee geofencing during check-in/out.
              </Typography>
            </Grid>



            <Grid size={12}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Subscription Plan</InputLabel>
                <Select
                  label="Subscription Plan"
                  {...register('subscription_plan')}
                  defaultValue="basic"
                  error={!!errors.subscription_plan}
                >
                  <MenuItem value="basic">Basic</MenuItem>
                  <MenuItem value="premium">Premium</MenuItem>
                  <MenuItem value="enterprise">Enterprise</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </form>
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{ mr: 1 }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          form="org-form"
          variant="contained"
          disabled={loading}
          sx={{ minWidth: 120 }}
        >
          {loading ? (editMode ? 'Updating...' : 'Registering...') :
            editMode ? 'Update Organization' : 'Register Organization'}
        </Button>
      </DialogActions>

      {/* Location Picker Modal */}
      <LocationPickerModal
        open={locationPickerOpen}
        onClose={() => setLocationPickerOpen(false)}
        onConfirm={(loc) => setGeoLocation(loc)}
        initialValues={geoLocation}
      />
    </Dialog>
  )
}

export default RegisterOrgForm